import { onUnmounted, type WatchSource, watch } from "vue"

/**
 * Reference-counted body scroll lock shared by every overlay (bottom sheets,
 * modals, dialogs, the search palette).
 *
 * Each overlay previously set and reset `document.body.style.overflow`
 * directly, so one overlay closing unlocked the body underneath another that
 * was still open (action sheet → edit modal), and an unmount-while-open left
 * the page scroll-locked forever. The count lives on the body element itself
 * so all component instances share it.
 */
type LockableBody = typeof document.body & { __scrollLockCount?: number }

function lockBody() {
  const body = document.body as LockableBody
  body.__scrollLockCount = (body.__scrollLockCount ?? 0) + 1
  body.style.overflow = "hidden"
}

function unlockBody() {
  const body = document.body as LockableBody
  const next = Math.max(0, (body.__scrollLockCount ?? 0) - 1)
  body.__scrollLockCount = next
  if (next === 0) body.style.overflow = ""
}

/**
 * Lock body scrolling while `isOpen` is true. Releases automatically when it
 * turns false and on component unmount.
 */
export function useBodyScrollLock(isOpen: WatchSource<boolean>) {
  let hasLock = false
  const applyLockState = (open: boolean) => {
    if (open && !hasLock) {
      lockBody()
      hasLock = true
    } else if (!open && hasLock) {
      unlockBody()
      hasLock = false
    }
  }

  watch(isOpen, applyLockState, { immediate: true })
  onUnmounted(() => applyLockState(false))
}
